/**
 * Parallel, index-aligned enum value lists for the processor's single-value-per-locale enum
 * fields (index i means the same underlying concept in en/sv/fi). Shared between the extraction
 * schema in processor.ts and reconcileSingleValueLocaleEnums, which derives sv/fi from whichever
 * value en picked rather than trusting three independent per-locale LLM generations to agree —
 * confirmed via manual review that they sometimes don't (e.g. peopleManagementTier_en/sv said
 * "Manager of Managers" while peopleManagementTier_fi said "No people management" on the same
 * job). Keeping these lists in one place also prevents the kind of silent misalignment found
 * during that same review: remoteness_sv/fi were previously ordered differently from
 * remoteness_en, and listingCompanyIndustry_sv/fi were an entirely different taxonomy.
 */

export const NORMALISED_ROLE_LEVEL_EN = [
  'Intern', 'Junior', 'Mid-Level', 'Senior', 'Expert', 'Manager', 'Director', 'Executive',
];
export const NORMALISED_ROLE_LEVEL_SV = [
  'Praktikant', 'Junior', 'Medelnivå', 'Senior', 'Expert', 'Chef', 'Direktör', 'Företagsledare',
];
export const NORMALISED_ROLE_LEVEL_FI = [
  'Harjoittelija', 'Juniori', 'Keskitaso', 'Senior-taso', 'Asiantuntija', 'Esimies', 'Johtaja', 'Ylin johto',
];

// Broad work-domain buckets for field_*. Covers both office/knowledge-work functions and the
// blue-collar/service trades that dominate the actual Finnish listing volume (construction,
// hospitality, cleaning, retail, manufacturing, security) — a prior unconstrained free-text
// field_* let the model drift to granular job titles ("Cook", "Welder", "Building Cleaner")
// instead of these buckets, with "Other" absorbing most of them. Index-aligned with the
// keyword buckets in infer-processor-work-field-from-title.ts, which draws its triplets from
// these same arrays instead of duplicating them.
export const FIELD_EN = [
  'Software engineering', 'Data & analytics', 'Healthcare', 'Education', 'Legal',
  'Finance & accounting', 'Sales', 'Marketing', 'Human resources', 'Customer support',
  'Design', 'Product', 'Operations & supply chain', 'Construction & trades',
  'Hospitality & food service', 'Cleaning & property services', 'Retail',
  'Manufacturing & industrial', 'Security services', 'Technology', 'Other',
];
export const FIELD_SV = [
  'Mjukvaruutveckling', 'Data & analys', 'Hälsovård', 'Utbildning', 'Juridik',
  'Ekonomi & redovisning', 'Försäljning', 'Marknadsföring', 'HR', 'Kundsupport',
  'Design', 'Produkt', 'Logistik & inköp', 'Bygg & installation',
  'Restaurang & hotell', 'Städ & fastighetsservice', 'Detaljhandel',
  'Tillverkning & industri', 'Säkerhetstjänster', 'Teknologi', 'Övrigt',
];
export const FIELD_FI = [
  'Ohjelmistokehitys', 'Data ja analytiikka', 'Terveysala', 'Koulutusala', 'Lakiala',
  'Talous ja kirjanpito', 'Myynti', 'Markkinointi', 'Henkilöstö', 'Asiakaspalvelu',
  'Design', 'Tuote', 'Logistiikka ja osto', 'Rakennus- ja asennusala',
  'Ravintola- ja majoitusala', 'Siivous- ja kiinteistöpalvelut', 'Vähittäiskauppa',
  'Teollisuus ja tuotanto', 'Vartiointi ja turvallisuus', 'Teknologia', 'Muu ala',
];

export const REMOTENESS_EN = ['Remote', 'Hybrid', 'Onsite'];
export const REMOTENESS_SV = ['Distans', 'Hybrid', 'På plats'];
export const REMOTENESS_FI = ['Etätyö', 'Hybridityö', 'Paikkasidonnainen työ'];

export const PEOPLE_MANAGEMENT_TIER_EN = ['No People Management', 'People Manager', 'Manager of Managers'];
export const PEOPLE_MANAGEMENT_TIER_SV = ['Inget personalansvar', 'Chef med personalansvar', 'Chef över chefer'];
export const PEOPLE_MANAGEMENT_TIER_FI = ['Ei esimiestehtäviä', 'Esimies', 'Esimiesten esimies'];

export const JOB_TYPE_EN = [
  'Full-time', 'Part-time', 'Temporary', 'Summer Job', 'Contract', 'Internship',
  'Open Application', 'Freelance', 'Apprenticeship',
];
export const JOB_TYPE_SV = [
  'Heltid', 'Deltid', 'Tillfällig', 'Sommarjobb', 'Uppdrag', 'Praktik',
  'Öppen ansökan', 'Frilans', 'Läroavtal',
];
export const JOB_TYPE_FI = [
  'Kokoaikainen', 'Osa-aikainen', 'Määräaikainen', 'Kesätyö', 'Sopimuspohjainen työ', 'Harjoittelupaikka',
  'Avoin hakemus', 'Keikkatyö', 'Oppisopimus',
];

// The "no requirement" value is phrased to match actual job-search queries ("no degree
// required" / "ei koulutusta vaadita" / "ingen utbildning krävs") rather than a generic
// "none" — see the educationLevel backfill script for the historical-data migration.
export const EDUCATION_LEVEL_EN = [
  'No degree required', 'High school', 'Bachelor', 'Masters', 'Doctorate', 'Apprenticeship', 'Diploma',
];
export const EDUCATION_LEVEL_SV = [
  'Ingen utbildning krävs', 'Gymnasieutbildning', 'Kandidat', 'Magister', 'Doktorsexamen', 'Läroavtalsutbildning', 'Diplom',
];
export const EDUCATION_LEVEL_FI = [
  'Ei koulutusta vaadita', 'Toisen asteen koulutus', 'Alempi korkeakoulututkinto', 'Ylempi korkeakoulututkinto',
  'Tieteellinen jatkotutkinto', 'Oppisopimuskoulutus', 'Diplomi',
];

export const LISTING_COMPANY_INDUSTRY_EN = [
  'Technology', 'Finance', 'Retail', 'Gaming', 'E-commerce', 'Consulting', 'Real Estate', 'Automotive',
  'Entertainment', 'Healthcare', 'Education', 'Public Sector & Government', 'Manufacturing', 'Construction',
  'Hospitality & Tourism', 'Transportation & Logistics', 'Agriculture & Forestry', 'Non-profit',
  'Energy & Utilities', 'Professional Services', 'Telecommunications', 'Media & Publishing',
];
export const LISTING_COMPANY_INDUSTRY_SV = [
  'Teknik', 'Finans', 'Detaljhandel', 'Spelbranschen', 'E-handel', 'Konsultverksamhet', 'Fastighetsbranschen',
  'Fordonsindustrin', 'Underhållningsbranschen', 'Hälso- och sjukvård', 'Utbildning', 'Offentlig förvaltning',
  'Tillverkningsindustri', 'Byggbranschen', 'Turism och besöksnäring', 'Transport och logistik',
  'Jord- och skogsbruk', 'Ideell sektor', 'Energisektorn', 'Professionella tjänster', 'Telekommunikation',
  'Media och förlag',
];
export const LISTING_COMPANY_INDUSTRY_FI = [
  'Teknologia', 'Finanssiala', 'Vähittäiskauppa', 'Peliala', 'Verkkokauppa', 'Konsultointi', 'Kiinteistöala',
  'Autoteollisuus', 'Viihdeala', 'Terveydenhuolto', 'Koulutus', 'Julkishallinto', 'Valmistava teollisuus',
  'Rakennusala', 'Matkailu- ja majoitusala', 'Kuljetus ja logistiikka', 'Maa- ja metsätalous', 'Kolmas sektori',
  'Energia-ala', 'Asiantuntijapalvelut', 'Televiestintä', 'Media ja julkaisuala',
];

/** One entry per single-value locale-enum field group, for reconcileSingleValueLocaleEnums. */
export const LOCALE_ENUM_FIELD_GROUPS = [
  { base: 'normalisedRoleLevel', en: NORMALISED_ROLE_LEVEL_EN, sv: NORMALISED_ROLE_LEVEL_SV, fi: NORMALISED_ROLE_LEVEL_FI },
  { base: 'field', en: FIELD_EN, sv: FIELD_SV, fi: FIELD_FI },
  { base: 'remoteness', en: REMOTENESS_EN, sv: REMOTENESS_SV, fi: REMOTENESS_FI },
  { base: 'peopleManagementTier', en: PEOPLE_MANAGEMENT_TIER_EN, sv: PEOPLE_MANAGEMENT_TIER_SV, fi: PEOPLE_MANAGEMENT_TIER_FI },
  { base: 'jobType', en: JOB_TYPE_EN, sv: JOB_TYPE_SV, fi: JOB_TYPE_FI },
  { base: 'educationLevel', en: EDUCATION_LEVEL_EN, sv: EDUCATION_LEVEL_SV, fi: EDUCATION_LEVEL_FI },
  { base: 'listingCompanyIndustry', en: LISTING_COMPANY_INDUSTRY_EN, sv: LISTING_COMPANY_INDUSTRY_SV, fi: LISTING_COMPANY_INDUSTRY_FI },
] as const;
